import { useEffect, useRef, useCallback, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Typography, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import smoothingImg from '../../../../src/resources/smoothing2.jpg';

/**
 * @function KernelGrid
 * @description Renders a visual representation of a normalized Gaussian kernel. The kernel is generated using the provided radius and sigma values,
 * normalized so that all weights sum to 1, and displayed as a heatmap-like grid where darker cells represent higher weights.
 * @param {number} props.radius - Kernel radius. The resulting kernel size is `(2 × radius + 1)`.
 * @param {number} props.sigma - Standard deviation (σ) of the Gaussian distribution.
 * @param {Object} props.classes - Material-UI style classes.
 * **Called by:
 * @see {SmoothingPreview}
 * @memberof module:Bathymetry
 * @returns {JSX.Element} A grid-based preview of the Gaussian kernel.
 */
const KernelGrid = ({ radius, sigma, classes }) => {
  const size = 2 * radius + 1;
  const kernel = [];
  let sum = 0;
  for (let y = -radius; y <= radius; y++) {
    for (let x = -radius; x <= radius; x++) {
      const v = Math.exp(-(x * x + y * y) / (2 * sigma * sigma));
      kernel.push(v);
      sum += v;
    }
  }
  const normalized = kernel.map((v) => v / sum);
  const max = Math.max(...normalized);
  const displaySize = Math.min(size, 7);
  const offset = Math.floor((size - displaySize) / 2);
  const cells = [];
  for (let row = 0; row < displaySize; row++) {
    for (let col = 0; col < displaySize; col++) {
      const r = offset + row;
      const c = offset + col;
      const val = normalized[r * size + c] / max;
      cells.push(val);
    }
  }
  const cellSize = Math.max(12, Math.floor(110 / displaySize));
  return (
    <Box
      className={classes.kernelGrid}
      style={{
        gridTemplateColumns: `repeat(${displaySize}, ${cellSize}px)`,
        gridTemplateRows: `repeat(${displaySize}, ${cellSize}px)`,
      }}>
      {cells.map((val, i) => (
        <Box
          key={i}
          style={{
            width: cellSize,
            height: cellSize,
            borderRadius: 2,
            backgroundColor: `rgba(25, 118, 210, ${0.08 + val * 0.88})`,
          }}
        />
      ))}
    </Box>
  );
};

/**
 * @function SmoothingPreview
 * @description Renders an image that responds to sigma and radius change to make a preview of a gaussian smoothing/blurring.
 * Provides range controls for adjusting kernel parameters
 * @param {number} radius - gaussian kernel radius
 * @param {number} sigma - gaussian sigma (σ).
 * @param {Function} onRadiusChange - (value: number) => void. Callback triggered when the radius changes.
 * @param {Function} onSigmaChange - (value: number) => void. Callback triggered when the sigma changes.
 * @param {Function} onGenerate - () => void. Callback triggered when the smoothing operation is requested.
 * @param {boolean} loading - Indicates whether the smoothing process is running.
 * @param {number} selectedCount - Number of selected images that will be processed.
 * @see {@link KernelGrid} - renders the kernel preview grid alongside the image.
 * @see {@link renderBlurred} - re-renders the blurred canvas whenever radius/sigma change.
 * **Called by:
 * @see {PeriodChooser}
 * @memberof module:Bathymetry
 * @returns {JSX.Element} Gaussian smoothing preview interface
 */
const SmoothingPreview = ({
  radius,
  sigma,
  onRadiusChange,
  onSigmaChange,
  onGenerate,
  loading = false,
  selectedCount = 0,
}) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const canvasRef = useRef(null);
  const originalDataRef = useRef(null);
  const [imageReady, setImageReady] = useState(false);
  const debounceRef = useRef(null);

  /**
   * @function buildGaussianKernel
   * @description Generates a normalized 2D Gaussian convolution kernel. The kernel size is determined by the radius parameter: size = (2 × radius + 1)`.
   * @param {number} radius - Kernel radius. Must be greater than or equal to 0.
   * @param {number} sigma - Gaussian standard deviation (σ). Must be greater than 0.
   * **Called by:
   * @see {applyGaussian}
   * @memberof module:Bathymetry
   * @returns {Float32Array} Normalized Gaussian kernel stored in row-major order.
   */
  const buildGaussianKernel = (radius, sigma) => {
    const size = 2 * radius + 1;
    const kernel = new Float32Array(size * size);
    let sum = 0;
    for (let y = -radius; y <= radius; y++) {
      for (let x = -radius; x <= radius; x++) {
        const v = Math.exp(-(x * x + y * y) / (2 * sigma * sigma));
        kernel[(y + radius) * size + (x + radius)] = v;
        sum += v;
      }
    }
    for (let i = 0; i < kernel.length; i++) kernel[i] /= sum;
    return kernel;
  };

  /**
   * @function applyGaussian
   * @description Applies a Gaussian blur to each pixel in the image using a convolution kernel
   * each pixel is recalculated as the weighted sum of its neighboring pixels. Border pixels are
   * handled using edge clamping, ensuring that kernel samples outside the image bounds use the nearest valid pixel.
   * The blur is appied to the Red, Green, Blue and Alpha channels.
   * @param {Uint8ClampedArray} srcData - Source image pixel data in RGBA format.
   * @param {number} width - Image width in pixels.
   * @param {number} height - Image height in pixels.
   * @param {number} radius - Gaussian kernel radius.
   * @param {number} sigma - Gaussian standard deviation (σ). Must be greater than 0.
   * @see {@link buildGaussianKernel} - builds the normalized convolution kernel applied here.
   * **Called by:
   * @see {renderBlurred}
   * @memberof module:Bathymetry
   * @returns {Uint8ClampedArray} A new pixel buffer containing the blurred image data.
   */
  const applyGaussian = useCallback((srcData, width, height, radius, sigma) => {
    const kernel = buildGaussianKernel(radius, sigma);
    const size = 2 * radius + 1;
    const dst = new Uint8ClampedArray(srcData.length);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let r = 0,
          g = 0,
          b = 0,
          a = 0;
        for (let ky = -radius; ky <= radius; ky++) {
          for (let kx = -radius; kx <= radius; kx++) {
            const px = Math.min(Math.max(x + kx, 0), width - 1);
            const py = Math.min(Math.max(y + ky, 0), height - 1);
            const idx = (py * width + px) * 4;
            const w = kernel[(ky + radius) * size + (kx + radius)];
            r += srcData[idx] * w;
            g += srcData[idx + 1] * w;
            b += srcData[idx + 2] * w;
            a += srcData[idx + 3] * w;
          }
        }
        const oi = (y * width + x) * 4;
        dst[oi] = r;
        dst[oi + 1] = g;
        dst[oi + 2] = b;
        dst[oi + 3] = a;
      }
    }
    return dst;
  }, []);

  /**
   * @function renderBlurred
   * @description Applies gaussian in the canvas.The preview is generated using the image data stored in
   * originalDataRef.current and rendered to canvasRef.current.
   * @see {@link applyGaussian} - performs the actual per-pixel convolution.
   * **Called by:
   * @see {SmoothingPreview} - via the debounced radius/sigma-change effect.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const renderBlurred = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalDataRef.current) return;
    const ctx = canvas.getContext('2d');
    const { data, width, height } = originalDataRef.current;
    if (radius <= 1 && sigma <= 0.5) {
      ctx.putImageData(new ImageData(new Uint8ClampedArray(data), width, height), 0, 0);
      return;
    }
    const blurred = applyGaussian(data, width, height, radius, sigma);
    ctx.putImageData(new ImageData(blurred, width, height), 0, 0);
  }, [radius, sigma, applyGaussian]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const maxW = 256;
      const scale = Math.min(1, maxW / img.naturalWidth);
      const W = Math.round(img.naturalWidth * scale);
      const H = Math.round(img.naturalHeight * scale);
      canvas.width = W;
      canvas.height = H;
      ctx.drawImage(img, 0, 0, W, H);
      originalDataRef.current = {
        data: new Uint8ClampedArray(ctx.getImageData(0, 0, W, H).data),
        width: W,
        height: H,
      };
      setImageReady(true);
    };
    img.onerror = () => {
      canvas.width = 256;
      canvas.height = 300;
      ctx.fillStyle = '#0d1f18';
      ctx.fillRect(0, 0, 256, 300);
      for (let i = 0; i < 1400; i++) {
        const x = Math.random() * 256;
        const y = Math.random() * 300;
        const lum = 20 + Math.floor(Math.random() * 100);
        ctx.fillStyle = `rgba(${(lum * 0.3) | 0},${lum},${(lum * 0.55) | 0},0.35)`;
        ctx.beginPath();
        ctx.arc(x, y, Math.random() * 18 + 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = 'rgba(0,15,10,0.45)';
      ctx.fillRect(0, 0, 256, 300);
      originalDataRef.current = {
        data: new Uint8ClampedArray(ctx.getImageData(0, 0, 256, 300).data),
        width: 256,
        height: 300,
      };
      setImageReady(true);
    };
    img.src = smoothingImg;
  }, []);

  useEffect(() => {
    if (!imageReady) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(renderBlurred, 80);
    return () => clearTimeout(debounceRef.current);
  }, [radius, sigma, imageReady, renderBlurred]);

  const kernelSize = 2 * radius + 1;

  const smoothLabel =
    sigma < 1
      ? t('forms.acquisition.3.bathymetry.smoothingVeryLight')
      : sigma < 2
        ? t('forms.acquisition.3.bathymetry.smoothingModerate')
        : sigma < 3.5
          ? t('forms.acquisition.3.bathymetry.smoothingStrong')
          : t('forms.acquisition.3.bathymetry.smoothingVeryStrong');

  return (
    <Box className={classes.root}>
      <Box className={classes.topRow}>
        <Box className={classes.canvasWrapper}>
          <canvas ref={canvasRef} className={classes.canvas} />
        </Box>
        <Box className={classes.kernelInfo}>
          <Typography variant="caption" className={classes.kernelTitle}>
            {t('forms.acquisition.3.bathymetry.kernelLabel')}
          </Typography>
          <Typography variant="h6" className={classes.kernelSize}>
            {kernelSize}×{kernelSize}
          </Typography>
          <KernelGrid radius={radius} sigma={sigma} classes={classes} />
          <Typography variant="caption" className={classes.smoothLabel}>
            {smoothLabel}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.slidersRow}>
        <Box className={classes.sliderGroup}>
          <Box className={classes.sliderLabel}>
            <Typography variant="caption" className={classes.sliderLabelText}>
              {t('forms.acquisition.3.bathymetry.smoothSigma')}
            </Typography>
            <Typography variant="caption" className={classes.sliderValue}>
              {sigma.toFixed(1)}
            </Typography>
          </Box>
          <input
            type="range"
            min={0.1}
            max={2}
            step={0.1}
            value={sigma}
            className={classes.slider}
            onChange={(e) => onSigmaChange(Number(e.target.value))}
          />
        </Box>
        <Box className={classes.sliderGroup}>
          <Box className={classes.sliderLabel}>
            <Typography variant="caption" className={classes.sliderLabelText}>
              {t('forms.acquisition.3.bathymetry.smoothRadius')}
            </Typography>
            <Typography variant="caption" className={classes.sliderValue}>
              {radius}
            </Typography>
          </Box>
          <input
            type="range"
            min={1}
            max={3}
            step={1}
            value={radius}
            className={classes.slider}
            onChange={(e) => onRadiusChange(Number(e.target.value))}
          />
        </Box>
      </Box>
      <Button
        variant="contained"
        size="large"
        disabled={loading || selectedCount === 0}
        fullWidth
        className={classes.generateBtn}
        onClick={onGenerate}
        style={{ backgroundColor: '#4A90D9', color: '#fff' }}>
        {loading ? (
          <>
            <CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} />
            {t('forms.acquisition.3.bathymetry.generatingImage')}
          </>
        ) : (
          `${t('forms.acquisition.3.bathymetry.generateImage')} (${selectedCount} ${t('forms.acquisition.3.bathymetry.images')})`
        )}
      </Button>
    </Box>
  );
};

/**
 * @function useStyles-SmoothingPreview
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles .
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Bathymetry
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    width: '100%',
  },
  topRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
  },
  canvasWrapper: {
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
    lineHeight: 0,
    flex: 1,
    minWidth: 0,
  },
  canvas: {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxWidth: 'none',
  },
  kernelInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 4,
    width: 120,
    height: 120,
    flexShrink: 0,
    alignSelf: 'center',
  },
  kernelTitle: {
    color: theme.palette.text.secondary,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  kernelSize: {
    fontWeight: 700,
    fontSize: 20,
    color: '#4A90D9',
    lineHeight: 1,
  },
  kernelGrid: {
    display: 'grid',
    gap: 2,
    marginTop: 2,
  },
  smoothLabel: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    marginTop: 2,
  },
  slidersRow: {
    display: 'flex',
    gap: theme.spacing(3),
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
  },
  sliderGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },
  sliderLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sliderLabelText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  sliderValue: {
    fontWeight: 600,
    fontSize: 13,
  },
  slider: {
    width: '100%',
    cursor: 'pointer',
    accentColor: '#4A90D9',
    appearance: 'none',
    height: 4,
    borderRadius: 2,
    background: '#dde8f5',
    outline: 'none',
  },
  generateBtn: {
    maxWidth: 520,
    alignSelf: 'center',
    width: '100%',
    '&:not(:disabled)': {
      backgroundColor: '#4A90D9',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#357ABD',
      },
    },
  },
}));

export default SmoothingPreview;
